/**
 * InviteCreateScreen — crea un invite link C2C opaco.
 *
 * L'emittente sceglie livello whitelist, durata, monouso/multiuso, e
 * un'etichetta locale (memo, resta solo sul suo dispositivo). Il link non
 * rivela chi sei: il destinatario lo scopre solo accettando (redeem).
 *
 * Mostra anche un QR code del link, generato localmente (no servizi
 * esterni) cosi' si puo' far inquadrare al destinatario di persona.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Frame, Header, Footer, Btn, Box, FieldLabel } from '../components/Frame';
import { createInvite, listMyInvites, revokeInvite, confirmRedemption, deleteInvite } from '../lib/messaging';
import { getMyAccount } from '../lib/derivedSigil';
import { auth, onAuthStateChanged } from '../lib/firebase';
import { useStore } from '../lib/store';

const WL_OPTIONS = [
  { value: 'request', label: 'su richiesta (accetti tu)' },
  { value: 'full', label: 'diretto (può scriverti subito)' },
];
// TTL in MINUTI. Ordine: dal piu' effimero al piu' duraturo.
const TTL_OPTIONS = [
  { value: 1, label: '1 minuto (quasi temporaneo)' },
  { value: 5, label: '5 minuti' },
  { value: 60, label: '1 ora' },
  { value: 1440, label: '24 ore' },
  { value: 4320, label: '3 giorni' },
  { value: 10080, label: '7 giorni' },
];

function formatTtl(min) {
  return TTL_OPTIONS.find((o) => o.value === min)?.label || `${min} min`;
}

function statusInfo(s) {
  switch (s) {
    case 'active': return { t: 'attivo', c: 'text-ink text-glow' };
    case 'used': return { t: 'usato', c: 'text-ink-dim' };
    case 'expired': return { t: 'scaduto', c: 'text-ink-dim' };
    case 'revoked': return { t: 'revocato', c: 'text-red-400' };
    default: return { t: s, c: 'text-ink-dim' };
  }
}
function localLabel(token) {
  try { return localStorage.getItem('nullum.inviteLabel.' + token) || ''; } catch { return ''; }
}
function fmtExpiry(ms) {
  const diff = ms - Date.now();
  if (diff <= 0) return 'scaduto';
  const h = Math.round(diff / 3600000);
  if (h < 1) return 'scade tra <1h';
  if (h < 48) return `scade tra ${h}h`;
  return `scade tra ${Math.round(h / 24)}g`;
}

export default function InviteCreateScreen() {
  const navigate = useNavigate();
  const { sigil: masterSigil, derivedKeys, premium, betaTester } = useStore();
  const [whitelistLevel, setWhitelistLevel] = useState('request');
  const [ttlMinutes, setTtlMinutes] = useState(1440); // default 24h
  const [multiUse, setMultiUse] = useState(false);
  const [allowReinvite, setAllowReinvite] = useState(false); // default: l'invitato NON può invitare
  const [requirePasscode, setRequirePasscode] = useState(false); // invito "a distanza" con codice
  const [copiedToken, setCopiedToken] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [qrModal, setQrModal] = useState(null); // { token, dataUrl } QR di un invito della lista
  const [isBetaTester, setIsBetaTester] = useState(betaTester); // autoritativo via getMyAccount
  const [account, setAccount] = useState(null); // { role, canInvite, inviteLimit, inviteUsedThisMonth, inviteRemaining }
  const [label, setLabel] = useState('');
  const [emitAs, setEmitAs] = useState(''); // '' = master, altrimenti sigilLower del derivato
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [link, setLink] = useState(null);
  const [linkPasscode, setLinkPasscode] = useState(null); // passcode dell'invito appena creato
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const qrAnchorRef = useRef(null);
  const [invites, setInvites] = useState(null);
  const [invBusy, setInvBusy] = useState(null);
  const [showInvites, setShowInvites] = useState(false);

  const derivedList = Object.values(derivedKeys || {}); // [{sigil, ...}]

  async function onCreate() {
    setErr(null);
    setBusy(true);
    try {
      const r = await createInvite({
        ttlMinutes, whitelistLevel, multiUse,
        emitAsSigil: emitAs || null,
        asBetaTester: !allowReinvite, // se NON può invitare → è un "beta tester"
        requirePasscode,
      });
      if (!r?.token) throw new Error(r?.error || 'creazione fallita');
      // Forziamo sempre il dominio pubblico produzione (nullum.app), anche se
      // l'utente sta usando ancora la vecchia URL Netlify o un dev server.
      // Il server ritorna gia' r.url = 'https://nullum.app/i/<token>': se c'e'
      // lo usiamo dritto; altrimenti costruiamo a mano.
      const url = r.url || `https://nullum.app/i/${r.token}`;
      setLink(url);
      setLinkPasscode(r.passcode || null);
      if (label.trim()) {
        try { localStorage.setItem(`nullum.inviteLabel.${r.token}`, label.trim()); } catch {}
      }
      refreshInvites(); // il nuovo invito comparira' nella lista al ritorno
      refreshAccount(); // aggiorna la quota residua del mese
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('BETA_TESTER_NO_INVITE')) setErr('I beta tester non possono creare inviti.');
      else if (m.includes('INVITE_QUOTA_EXCEEDED')) {
        setErr('Hai raggiunto il limite di 5 inviti per questo mese. Il contatore si azzera il 1° del mese prossimo.');
        refreshAccount();
      } else setErr(e?.message || 'errore');
    } finally {
      setBusy(false);
    }
  }

  // Carica i miei inviti all'apertura della schermata.
  useEffect(() => { refreshInvites(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Stato beta-tester AUTORITATIVO: lo rileggiamo dal server appena l'auth è
  // pronta, così il blocco vale anche su deep-link/reload (store non popolato).
  useEffect(() => {
    const off = onAuthStateChanged(auth(), (u) => {
      if (!u) return;
      getMyAccount().then((a) => { setIsBetaTester(!!a.betaTester); setAccount(a); }).catch(() => {});
    });
    return off;
  }, []);
  async function refreshAccount() {
    try { setAccount(await getMyAccount()); } catch { /* non bloccante */ }
  }
  async function refreshInvites() {
    // Gli inviti REVOCATI sono "morti" (non più riscattabili): non li mostriamo
    // nella lista. Il doc resta sul server con revoked:true così il redeem del
    // token revocato continua a essere rifiutato.
    try { setInvites((await listMyInvites()).filter((i) => i.status !== 'revoked')); }
    catch { setInvites([]); }
  }
  async function doRevoke(token) {
    if (!confirm(
      'Revocare questo invito?\n\n'
      + 'Chi NON l\'ha ancora accettato non potra\' piu\' usarlo. Chi l\'ha gia\' '
      + 'accettato resta tra i tuoi contatti (rimuovilo da "Contatti pre-approvati").'
    )) return;
    setInvBusy(token);
    setErr(null);
    try { await revokeInvite(token); await refreshInvites(); }
    catch (e) { setErr(e?.message || 'revoca fallita'); }
    finally { setInvBusy(null); }
  }
  // Conferma/rifiuta un riscatto in attesa (invito monouso): solo dopo la
  // conferma chi ha riscattato diventa contatto pre-approvato.
  async function doConfirm(token, accept) {
    if (!accept && !confirm(
      'Rifiutare questo riscatto?\n\n'
      + 'Chi ha usato il link NON diventera\' un tuo contatto pre-approvato. '
      + 'Usalo se non riconosci chi ha riscattato.'
    )) return;
    setInvBusy(token);
    setErr(null);
    try { await confirmRedemption(token, accept); await refreshInvites(); }
    catch (e) { setErr(e?.message || 'operazione fallita'); }
    finally { setInvBusy(null); }
  }
  // Cancella definitivamente un invito scaduto/usato dalla lista.
  async function doRemove(token) {
    setInvBusy(token);
    setErr(null);
    try { await deleteInvite(token); await refreshInvites(); }
    catch (e) { setErr(e?.message || 'cancellazione fallita'); }
    finally { setInvBusy(null); }
  }
  // Rimuove in blocco gli inviti non più attivi (scaduti/usati), escludendo
  // quelli con un riscatto ancora da confermare.
  async function doRemoveExpired() {
    const dead = invites.filter((i) => i.status !== 'active' && i.redeemConfirmStatus !== 'pending');
    if (dead.length === 0) return;
    if (!confirm(`Rimuovere ${dead.length} invito/i non più attivo/i dalla lista?`)) return;
    setInvBusy('__bulk__');
    setErr(null);
    try {
      for (const i of dead) { try { await deleteInvite(i.token); } catch {} }
      await refreshInvites();
    } catch (e) { setErr(e?.message || 'pulizia fallita'); }
    finally { setInvBusy(null); }
  }
  // QR del link di un invito della lista (per mostrarlo di persona).
  async function showInviteQr(token) {
    try {
      const url = await QRCode.toDataURL(`https://nullum.app/i/${token}`, {
        errorCorrectionLevel: 'M', margin: 1, width: 320,
        color: { dark: '#22c55e', light: '#000000' },
      });
      setQrModal({ token, dataUrl: url });
    } catch (e) { console.warn('QR list gen failed', e); }
  }

  // Genera il QR code una volta che abbiamo il link.
  useEffect(() => {
    if (!link) { setQrDataUrl(null); return; }
    let cancelled = false;
    QRCode.toDataURL(link, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320,
      color: { dark: '#22c55e', light: '#000000' }, // verde su nero, stile terminale
    }).then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch((e) => console.warn('QR gen failed', e));
    return () => { cancelled = true; };
  }, [link]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }

  async function shareLink() {
    try {
      if (navigator.share) await navigator.share({ title: 'Nullum', text: 'Connettiti con me su Nullum', url: link });
      else copyLink();
    } catch {}
  }

  function saveQr() {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `nullum-invito-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <Frame statusLeft="INVITO" statusRight="5G">
      <Header title="// Crea invito" crumb="◀ indietro" onCrumbClick={() => navigate('/inbox')} />

      {qrModal && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
          onClick={() => setQrModal(null)}
        >
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-ink text-[14px]">Fai inquadrare questo QR per accettare l'invito</p>
            <div className="border border-ink-dim/50 rounded-xl p-2 bg-black">
              <img
                src={qrModal.dataUrl}
                alt="QR invito"
                width={280}
                height={280}
                className="block"
                style={{ imageRendering: 'pixelated' }}
              />
            </div>
            <div className="text-[11px] text-ink-dim break-all max-w-xs text-center select-all">
              https://nullum.app/i/{qrModal.token}
            </div>
            <Btn variant="ghost" onClick={() => setQrModal(null)}>Chiudi</Btn>
          </div>
        </div>
      )}

      {isBetaTester ? (
        <div className="space-y-4">
          <Box variant="warn" className="text-[14px] space-y-2">
            <p className="text-amber">🧪 Sei un beta tester</p>
            <p className="text-ink-dim">
              Per tenere la beta sotto controllo, i beta tester non possono creare inviti.
              Se ti serve far entrare qualcuno, chiedi a chi gestisce la beta.
            </p>
          </Box>
          <Footer>
            <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
          </Footer>
        </div>
      ) : !link ? (
        <div className="space-y-4">
          <p className="text-[15px] text-ink-dim">
            genera un link da dare a chi vuoi. chi lo apre si connette a te
            senza dover cercare il tuo sigil. il link non rivela chi sei.
          </p>

          <div>
            <FieldLabel>&gt; come lo consegni</FieldLabel>
            <select value={requirePasscode ? 'remote' : 'inperson'} onChange={(e) => setRequirePasscode(e.target.value === 'remote')}
              className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none">
              <option value="inperson">Di persona (QR) — nessun codice</option>
              <option value="remote">A distanza — con codice di sicurezza</option>
            </select>
            {requirePasscode ? (
              <p className="text-[12px] text-amber mt-1">
                genererò un <b>codice</b>: dallo alla persona su un canale DIVERSO dal link (a voce, di persona). Senza codice giusto il link non si attiva e non si brucia.
              </p>
            ) : (
              <p className="text-[12px] text-ink-dim mt-1">
                mostra il QR di persona. Consigliata una durata breve.
              </p>
            )}
          </div>

          {account && account.role === 'founder' && (
            <Box className="text-[13px]">
              👑 <b className="text-ink">Founder</b> · inviti illimitati.
            </Box>
          )}
          {account && account.role !== 'founder' && account.inviteLimit != null && (
            <Box className="text-[13px]">
              {account.role === 'builder' ? '🔗 Builder' : '· Membro'} · inviti questo mese:{' '}
              <span className="text-ink">{account.inviteUsedThisMonth}/{account.inviteLimit}</span>
              {account.inviteRemaining === 0
                ? ' — limite raggiunto, si azzera il 1° del mese prossimo.'
                : ` — te ne restano ${account.inviteRemaining}.`}
            </Box>
          )}

          {premium && derivedList.length > 0 && (
            <div>
              <FieldLabel>&gt; emetti l&apos;invito come</FieldLabel>
              <select value={emitAs} onChange={(e) => setEmitAs(e.target.value)}
                className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none">
                <option value="">@{masterSigil || '???'} (sigil principale)</option>
                {derivedList.map((d) => (
                  <option key={d.sigil} value={d.sigil}>@{d.sigil} (derivato)</option>
                ))}
              </select>
              {emitAs && (
                <p className="text-[12px] text-amber mt-1">
                  chi accetta vedra' SOLO @{emitAs}. Il tuo sigil principale @{masterSigil} resta nascosto.
                </p>
              )}
            </div>
          )}

          <div>
            <FieldLabel>&gt; chi accetta può scriverti</FieldLabel>
            <select value={whitelistLevel} onChange={(e) => setWhitelistLevel(e.target.value)}
              className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none">
              {WL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <FieldLabel>&gt; il link scade tra</FieldLabel>
            <select value={ttlMinutes} onChange={(e) => setTtlMinutes(Number(e.target.value))}
              className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none">
              {TTL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {ttlMinutes <= 5 && (
              <p className="text-[12px] text-amber mt-1">
                durata molto breve: dallo solo a chi sta per usarlo subito.
              </p>
            )}
          </div>

          <label className="flex items-center gap-3 text-[15px] text-ink-dim cursor-pointer">
            <input type="checkbox" checked={multiUse} onChange={(e) => setMultiUse(e.target.checked)}
              className="w-4 h-4 accent-green-500" />
            riutilizzabile da più persone (altrimenti vale una volta sola)
          </label>

          <label className="flex items-start gap-3 text-[15px] text-ink-dim cursor-pointer">
            <input type="checkbox" checked={allowReinvite} onChange={(e) => setAllowReinvite(e.target.checked)}
              className="w-4 h-4 mt-1 accent-green-500" />
            <span>🔗 chi accetta potrà <b className="text-ink">invitare altre persone</b> (builder). Lascia spento per un tester normale che non potrà invitare nessuno.</span>
          </label>

          <div>
            <FieldLabel>&gt; etichetta (solo per te, opzionale)</FieldLabel>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="es. Anna, commercialista…" maxLength={40}
              className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none" />
          </div>

          {err && <Box variant="danger" className="text-[14px]"><span className="text-red-500">✕</span> {err}</Box>}

          {invites && invites.length > 0 && (
            <div className="border-t border-ink-dim/40 pt-3">
              <button
                onClick={() => setShowInvites((v) => !v)}
                className="w-full flex justify-between items-center text-[14px] text-ink hover:text-glow"
              >
                <span>📋 I miei inviti ({invites.filter((i) => i.status === 'active').length} attivi / {invites.length})</span>
                <span className="text-ink-dim">{showInvites ? '▾' : '▸'}</span>
              </button>
              {showInvites && (
                <div className="mt-2 space-y-2">
                  {invites.some((i) => i.status !== 'active' && i.redeemConfirmStatus !== 'pending') && (
                    <button
                      onClick={doRemoveExpired}
                      disabled={invBusy === '__bulk__'}
                      className="w-full border border-ink-dim/50 rounded-lg text-ink-dim hover:text-ink hover:border-ink text-[12px] py-1.5 disabled:opacity-40 touch-manipulation"
                    >
                      {invBusy === '__bulk__' ? 'rimozione…' : '🧹 Rimuovi gli inviti scaduti / usati'}
                    </button>
                  )}
                  {invites.map((iv) => {
                    const si = statusInfo(iv.status);
                    const lbl = localLabel(iv.token);
                    return (
                      <div key={iv.token} className="border border-ink-dim/50 rounded-xl bg-ink/[0.015] p-3 text-[13px]">
                        <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-ink truncate">
                            {lbl || (iv.emitterSigil ? `@${iv.emitterSigil}` : 'invito')}
                            <span className={`ml-2 text-[11px] ${si.c}`}>· {si.t}</span>
                          </div>
                          <div className="text-[11px] text-ink-dim">
                            {iv.betaTester ? '🧪 tester (no inviti)' : '🔗 builder (può invitare)'}
                            {' · '}{iv.multiUse ? `multiuso ${iv.uses}/${iv.max}` : 'monouso'}
                            {iv.status === 'active' ? ` · ${fmtExpiry(iv.expiresAt)}` : ''}
                          </div>
                          {iv.passcode && (
                            <div className="text-[12px] mt-1">
                              🔑 codice: <span className="font-mono text-ink tracking-wider">{iv.passcode}</span>
                              <button
                                onClick={() => { try { navigator.clipboard?.writeText(iv.passcode); } catch {} }}
                                className="ml-2 text-ink-dim hover:text-ink"
                                title="copia codice"
                              >⧉</button>
                            </div>
                          )}
                        </div>
                        {iv.status === 'active' ? (
                          <div className="flex flex-col gap-1 shrink-0">
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(`https://nullum.app/i/${iv.token}`);
                                  setCopiedToken(iv.token);
                                  setTimeout(() => setCopiedToken(null), 1500);
                                } catch { /* clipboard non disponibile */ }
                              }}
                              className="border border-ink-dim/50 rounded-lg text-ink hover:border-ink text-[12px] px-2.5 py-1"
                            >
                              {copiedToken === iv.token ? '✓ copiato' : '⧉ Copia link'}
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(iv.token);
                                  setCopiedCode(iv.token);
                                  setTimeout(() => setCopiedCode(null), 1500);
                                } catch { /* clipboard non disponibile */ }
                              }}
                              title="copia solo il codice (senza nullum.app): più discreto. Chi lo riceve lo incolla in 'Ho un codice' nella schermata d'ingresso."
                              className="border border-ink-dim/50 rounded-lg text-ink hover:border-ink text-[12px] px-2.5 py-1"
                            >
                              {copiedCode === iv.token ? '✓ copiato' : '⧉ Copia codice'}
                            </button>
                            <button
                              onClick={() => showInviteQr(iv.token)}
                              className="border border-ink-dim/50 rounded-lg text-ink hover:border-ink text-[12px] px-2.5 py-1"
                            >
                              ▢ QR
                            </button>
                            <button
                              onClick={() => doRevoke(iv.token)}
                              disabled={invBusy === iv.token}
                              className="border border-redphos/50 rounded-lg text-redphos hover:bg-redphos/10 text-[12px] px-2.5 py-1 disabled:opacity-40"
                            >
                              {invBusy === iv.token ? '…' : 'Revoca'}
                            </button>
                          </div>
                        ) : iv.redeemConfirmStatus === 'pending' ? (
                          <span className="shrink-0 text-[11px] text-ink-dim">—</span>
                        ) : (
                          <button
                            onClick={() => doRemove(iv.token)}
                            disabled={invBusy === iv.token}
                            className="shrink-0 border border-ink-dim/50 rounded-lg text-ink-dim hover:text-ink hover:border-ink text-[12px] px-2.5 py-1 disabled:opacity-40 touch-manipulation"
                          >
                            {invBusy === iv.token ? '…' : '🗑 Rimuovi'}
                          </button>
                        )}
                        </div>

                        {iv.redeemConfirmStatus === 'pending' && (
                          <div className="mt-2 pt-2 border-t border-ink-dim/30">
                            <div className="text-[12px] text-ink">
                              🔔 <b>@{iv.pendingRedeemerSigil || '???'}</b> ha riscattato questo invito.
                            </div>
                            <div className="text-[11px] text-ink-dim mt-0.5">
                              Conferma <b>solo</b> se è la persona a cui hai mandato il link.
                              Finché non confermi, non diventa un tuo contatto pre-approvato.
                            </div>
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => doConfirm(iv.token, true)}
                                disabled={invBusy === iv.token}
                                className="border border-ink rounded-lg bg-ink/10 text-ink text-glow text-[12px] px-3 py-1 hover:bg-ink/20 transition-colors disabled:opacity-40"
                              >
                                {invBusy === iv.token ? '…' : '✓ Conferma contatto'}
                              </button>
                              <button
                                onClick={() => doConfirm(iv.token, false)}
                                disabled={invBusy === iv.token}
                                className="border border-redphos/50 rounded-lg text-redphos text-[12px] px-3 py-1 hover:bg-redphos/10 transition-colors disabled:opacity-40"
                              >
                                ✕ Rifiuta
                              </button>
                            </div>
                          </div>
                        )}
                        {iv.redeemConfirmStatus === 'confirmed' && (
                          <div className="mt-2 pt-2 border-t border-ink-dim/30 text-[11px] text-ink-dim">
                            ✓ contatto confermato{iv.pendingRedeemerSigil ? ` (@${iv.pendingRedeemerSigil})` : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <Footer>
            <Btn variant="primary" onClick={onCreate} disabled={busy}>
              {busy ? '...' : '✦ Genera link'}
            </Btn>
          </Footer>
        </div>
      ) : (
        <div className="space-y-4">
          <Box variant="success" className="text-[14px]">
            ✓ link creato. condividilo con chi vuoi:
          </Box>

          <div className="border border-ink-dim/50 rounded-xl bg-ink/[0.015] p-3 text-[14px] text-ink break-all">{link}</div>

          {linkPasscode && (
            <Box variant="warn" className="text-[14px]">
              <p className="text-amber mb-1">🔑 Codice di sicurezza</p>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[22px] tracking-[0.2em] text-ink text-glow">{linkPasscode}</span>
                <button
                  onClick={() => { try { navigator.clipboard?.writeText(linkPasscode); } catch {} }}
                  className="border border-ink-dim/50 rounded-lg text-ink hover:border-ink text-[12px] px-2.5 py-1"
                >⧉ copia</button>
              </div>
              <p className="text-[12px] text-ink-dim mt-1">
                dallo alla persona su un canale <b>diverso</b> dal link. Lo ritrovi sempre in "I miei inviti".
              </p>
            </Box>
          )}

          <div className="flex gap-2">
            <Btn onClick={copyLink}>{copied ? '✓ copiato' : '⧉ Copia'}</Btn>
            <Btn onClick={shareLink}>↗ Condividi</Btn>
          </div>

          <div ref={qrAnchorRef} className="flex flex-col items-center gap-2">
            <FieldLabel>&gt; QR code (inquadralo)</FieldLabel>
            <div className="border border-ink-dim/50 rounded-xl p-2 bg-black">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR invito" width={260} height={260}
                  className="block" style={{ imageRendering: 'pixelated' }} />
              ) : (
                <div className="w-[260px] h-[260px] flex items-center justify-center text-ink-dim text-[13px]">
                  generazione QR...
                </div>
              )}
            </div>
            {qrDataUrl && (
              <Btn variant="ghost" onClick={saveQr}>⬇ Salva QR come immagine</Btn>
            )}
          </div>

          <p className="text-[13px] text-ink-dim">
            {multiUse ? 'riutilizzabile da più persone' : 'valido per una sola persona'} ·
            scade tra {formatTtl(ttlMinutes)}.
          </p>
          <Footer>
            <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
          </Footer>
        </div>
      )}
    </Frame>
  );
}
